import React, { FC, useEffect, useState } from 'react';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import { Title } from '../../components/developers/page-components/Title';
import { ButtonComponent } from '../../components/ButtonComponent';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { CreateCustomKeyModal } from '../../components/api-clients/CreateCustomKeyModal';
import { useTranslation } from 'react-i18next';
import { fetchCustomKeyList, setCustomKeyPagination, useApiClientContext } from '../../store/api-client-context';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';

export const ApiClients: FC = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { t } = useTranslation();
	const { apiClientContextState, dispatch } = useApiClientContext();
	const {
		apiClientKeysList,
		isApiClientKeyListLoading,
		apiClientPageNumber,
		apiClientPageSize,
		apiClientTotalItemCount
	} = apiClientContextState;
	const [isCustomKeyModalOpen, setIsCustomKeyModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	useEffect(() => {
		void fetchCustomKeyList(dispatch, apiClientContextState);
	}, [apiClientPageNumber, apiClientPageSize]);

	const handleSuccess = async (): Promise<void> => {
		setIsCustomKeyModalOpen(false);
		await fetchCustomKeyList(dispatch, apiClientContextState);
		setModalState({
			isOpen: true,
			modalTitle: 'Create custom key',
			content: (
				<div className="fs-6">
					{t('API_CLIENTS.SUCCESS_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		setIsCustomKeyModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: t('API_CLIENTS.MODAL_TITLE'),
			content: (
				<div className="fs-6">
					{t('API_CLIENTS.FAIL_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setIsCustomKeyModalOpen(true);
			},
			onClose: closeModal
		});
	};

	const rowActions = (rowData): JSX.Element => {
		return (
			<div
				className="text-center text-muted fs-7 fw-bolder cursor-pointer"
				onClick={(): void => navigate(`${pathname}/${rowData?.original?.id}`)}
			>
				Edit
			</div>
		);
	};
	return (
		<>
			<ButtonComponent
				buttonName={t('API_CLIENTS.MODAL_TITLE')}
				additionalClass="btn-sm d-block ms-auto mb-5"
				onButtonClick={(): void => setIsCustomKeyModalOpen(true)}
			/>
			<Table
				name={TablesNames.apiClientTableData}
				data={apiClientKeysList ?? []}
				isLoading={isApiClientKeyListLoading}
				pageSize={apiClientPageSize}
				pageNumber={apiClientPageNumber}
				totalItemCount={apiClientTotalItemCount}
				setPagination={(value): void => setCustomKeyPagination(dispatch, value)}
				toolbarCustomAction={<Title title='Custom keys' additionalClass='fs-2 ms-2'/>}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={50}
			/>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isCustomKeyModalOpen}
				onClose={():void => setIsCustomKeyModalOpen(false)}
				modalTitle={t('API_CLIENTS.MODAL_TITLE')}
				backdrop="static"
				contentClassName="d-block"
				content={<CreateCustomKeyModal onSuccess={handleSuccess} onError={handleError}/>}
			/>
		</>
	);
};
